import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react"
import { fetchConsolePrefs, pushConsolePrefs } from "@/core/user-prefs"

// Lightweight, SSR-safe theme controller. next-themes is built for Next; here
// we control the no-flash script + class application ourselves so TanStack
// Start SSR hydrates without a theme mismatch. Mode is persisted to
// localStorage; "system" follows the OS preference live.

export type ThemeMode = "light" | "dark" | "system"

const STORAGE_KEY = "attend-console-theme"

type ThemeContextValue = {
  mode: ThemeMode
  resolved: "light" | "dark"
  setMode: (mode: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

// Inline <head> script — runs before first paint to set the `.dark` class from
// the persisted choice (or OS), preventing a light→dark flash on load.
export const themeInitScript = `(function(){try{var m=localStorage.getItem(${JSON.stringify(
  STORAGE_KEY,
)})||"system";var d=m==="dark"||(m==="system"&&matchMedia("(prefers-color-scheme: dark)").matches);document.documentElement.classList.toggle("dark",d);}catch(e){}})();`

function systemPrefersDark(): boolean {
  if (typeof window === "undefined") return false
  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>("system")
  const [resolved, setResolved] = useState<"light" | "dark">("light")

  // Hydrate from storage after mount (server can't read localStorage), then
  // overlay the account-synced theme so it follows the user across devices.
  // Best-effort: fetchConsolePrefs never throws — signed out / endpoint not
  // deployed yet → no-op and the local choice stands. The synced value is
  // written back to localStorage so the no-flash <head> script picks it up on
  // the next cold load.
  useEffect(() => {
    const stored = (localStorage.getItem(STORAGE_KEY) as ThemeMode | null) ?? "system"
    setModeState(stored)
    void fetchConsolePrefs().then((p) => {
      if (p.theme === "light" || p.theme === "dark" || p.theme === "system") {
        setModeState(p.theme)
        try {
          localStorage.setItem(STORAGE_KEY, p.theme)
        } catch {
          // ignore
        }
      }
    })
  }, [])

  // Apply the resolved theme to <html> and react to OS changes under "system".
  useEffect(() => {
    const apply = () => {
      const dark = mode === "dark" || (mode === "system" && systemPrefersDark())
      document.documentElement.classList.toggle("dark", dark)
      setResolved(dark ? "dark" : "light")
    }
    apply()
    if (mode !== "system") return
    const mq = window.matchMedia("(prefers-color-scheme: dark)")
    mq.addEventListener("change", apply)
    return () => mq.removeEventListener("change", apply)
  }, [mode])

  const setMode = (next: ThemeMode) => {
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // ignore storage failures (private mode etc.)
    }
    setModeState(next)
    // Sync to the user's global account prefs (best-effort, never throws).
    pushConsolePrefs({ theme: next })
  }

  return (
    <ThemeContext.Provider value={{ mode, resolved, setMode }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider")
  return ctx
}
